import { useCallback, useEffect } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { ChatSessionCrossTabEventKind } from "../utils/chat-session-cross-tab"

import { isFeatureEnabledInConfig } from "@/hooks/feature-utils"
import { useConfigQuery } from "@/hooks/use-config-query"
import { SettingsService } from "@/services/settings-service"

import {
  matchesChatSessionScope,
  publishChatSessionCrossTabEvent,
  subscribeChatSessionCrossTab,
} from "../utils/chat-session-cross-tab"
import {
  scheduleSessionListRefetch,
  SESSION_LIST_REFETCH_COALESCE_MS,
} from "../utils/session-list-refetch"

/**
 * Per-environment feature flag for cross-window session sync (issue #2548).
 * Defaults OFF: absent from `config.features` — or present and disabled —
 * means no channel is ever opened, nothing is ever published, nothing is ever
 * subscribed, and no refetch is ever triggered.
 */
export const CROSS_WINDOW_SESSION_SYNC_FEATURE_ID = "cross_window_session_sync"

/**
 * Trailing debounce window for the refetch. Deliberately NOT a resetting
 * debounce: the first hint of a burst schedules the single refetch and every
 * later hint inside the window is absorbed, so a continuous stream of hints
 * still refreshes on a fixed cadence instead of starving. Well inside the
 * issue's "about a second" bar. The window is shared by every listener on the
 * same list (see `session-list-refetch.ts`, issue #3323 item C2).
 */
export const CHAT_SESSION_SYNC_DEBOUNCE_MS = SESSION_LIST_REFETCH_COALESCE_MS

export type ChatSessionSyncScope = {
  sessionType: string
  projectId?: string | null
}

/**
 * The signed-in user's object id, or `null` when it cannot be resolved.
 *
 * `SettingsService.getInstance()` THROWS before the service is initialized
 * (and `claims` is an empty object until a token is decoded), so both cases
 * are folded into `null` here — which the cross-tab bus treats as "publish
 * nothing, accept nothing". Read fresh on every call rather than captured:
 * it is the identity guard's input, and a stale identity is precisely the
 * bug the guard exists to prevent.
 */
export function readCurrentUserOid(): string | null {
  try {
    const oid: unknown = SettingsService.getInstance().claims.oid
    if (typeof oid !== "string") return null
    const trimmed = oid.trim()
    return trimmed === "" ? null : trimmed
  } catch {
    return null
  }
}

/** Whether cross-window session sync is enabled for this environment. */
export function useChatSessionSyncEnabled(): boolean {
  const { data: config } = useConfigQuery()
  return isFeatureEnabledInConfig(config?.features, CROSS_WINDOW_SESSION_SYNC_FEATURE_ID)
}

/**
 * Returns a publisher for session-LEVEL changes (created / renamed /
 * deleted). A strict no-op while the flag is off, while the identity is
 * unresolvable, and where `BroadcastChannel` does not exist.
 *
 * Never call this per streamed chunk — the hint bus is for session-level
 * events only.
 */
export function useChatSessionSyncPublisher({
  sessionType,
  projectId,
}: ChatSessionSyncScope): (kind: ChatSessionCrossTabEventKind) => void {
  const enabled = useChatSessionSyncEnabled()

  return useCallback(
    (kind: ChatSessionCrossTabEventKind) => {
      if (!enabled) return
      publishChatSessionCrossTabEvent({
        userId: readCurrentUserOid(),
        kind,
        sessionType,
        projectId,
      })
    },
    [enabled, projectId, sessionType]
  )
}

/**
 * Subscribes this window to same-user hints for ONE session list and
 * refetches that list — and only that list — when one arrives.
 *
 * The refetch is a plain `invalidateQueries` on the very same
 * `queryKeys.sessionHistory` key the list already uses, so the server stays
 * the only source of row data: a receiving window can never render a session
 * it could not open, because it never learns about a session from the
 * message at all.
 */
export function useChatSessionSyncListener({ sessionType, projectId }: ChatSessionSyncScope): void {
  const enabled = useChatSessionSyncEnabled()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return

    return subscribeChatSessionCrossTab({
      getUserId: readCurrentUserOid,
      onEvent: (event) => {
        if (!matchesChatSessionScope(event, { sessionType, projectId })) return
        // Shared with every other listener on this list, so one hint is one
        // refetch per tab however many times the list is mounted — and the
        // rest of a burst is absorbed into the already-scheduled refetch.
        scheduleSessionListRefetch(queryClient, sessionType, projectId)
      },
    })
  }, [enabled, projectId, queryClient, sessionType])
}
